<script setup lang="ts">
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {handleKeyDownAndCallShortcutIfNeeded} from "@/utils/common/ShortCutsUtil.ts";
import {Ref, ref} from "vue";
import {type QBtnDropdown, QEditor, type QList} from "quasar";
import BigTextFieldWidgetUtil from "@/utils/editView/widgets/BigTextFieldWidgetUtil.ts";
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";

let props = defineProps<WidgetProps>();
const fieldId = props.field.id!;
let fieldUtil = new BigTextFieldWidgetUtil(props.presenter, props.fieldMetaData);

let editor = fieldUtil.formattedValue();
const editorRef: Ref<QEditor | undefined> = ref();

let editorFonts = {
  arial: 'Arial',
  arial_black: 'Arial Black',
  comic_sans: 'Comic Sans MS',
  courier_new: 'Courier New',
  impact: 'Impact',
  lucida_grande: 'Lucida Grande',
  times_new_roman: 'Times New Roman',
  verdana: 'Verdana'
};

async function onEditorKeyDown(event: KeyboardEvent) {
  if (props.presenter.shortcutsHandler)
    await handleKeyDownAndCallShortcutIfNeeded(event, props.presenter.shortcutsHandler)
}

async function exitFullscreen() {
  if (editorRef.value) {
    editorRef.value.exitFullscreen();
  }
}
</script>
<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType"
    :isSpanned="isSpanned" :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="isInsideComposite">
    <template v-slot:widget>
      <form autocomplete="off" class=" tw-w-full">
        <q-editor
          ref="editorRef"
          @keydown="onEditorKeyDown"
          :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
          dense
          @keydown.esc="exitFullscreen"
          class="tw-max-w-full tw-border-2 tw-overflow-x-auto tw-w-full nama-richText-toolbar nama-customize-dense-input nama-focused-input dark:tw-bg-namaBackground-deep-dark"
          :class="{'tw-border-red-700':props.presenter.fetchFieldErrorMsg(fieldId), 'nama-richText-field-input ': (!props.presenter.fetchFieldErrorMsg(fieldId) && !fieldUtil.isFieldDisabled(forceEnableField) && !fieldUtil.isFieldFocused.value), 'nama-richText-field-input-disabled':(!props.presenter.fetchFieldErrorMsg(fieldId) && fieldUtil.isFieldDisabled(forceEnableField)),'tw-border-primary-light': (!props.presenter.fetchFieldErrorMsg(fieldId) && fieldUtil.isFieldFocused.value)}"
          content-class="dark:tw-border-white tw-relative nama-prose-list" min-height="5rem"
          :model-value="editor" @update:model-value="fieldUtil.onPartialUserInput" @focus="fieldUtil.onFocus" @blur="fieldUtil.onBlur"
          :toolbar="[
                  ['speech'],
                      [
                        {
                          label: $q.lang.editor.align,
                          icon: $q.iconSet.editor.align,
                          fixedLabel: true,
                          list: 'only-icons',
                          options: ['left', 'center', 'right', 'justify']
                        }
                      ],
                      ['bold', 'italic', 'strike', 'underline', 'subscript', 'superscript'],
                      ['token', 'hr', 'link', 'custom_btn'],
                      ['fullscreen'],
                      [
                        {
                          label: $q.lang.editor.formatting,
                          icon: $q.iconSet.editor.formatting,
                          list: 'no-icons',
                          options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code']
                        },
                        {
                          label: $q.lang.editor.fontSize,
                          icon: $q.iconSet.editor.fontSize,
                          fixedLabel: true,
                          fixedIcon: true,
                          list: 'no-icons',
                          options: ['size-1', 'size-2', 'size-3', 'size-4', 'size-5', 'size-6', 'size-7']
                        },
                        {
                          label: $q.lang.editor.defaultFont,
                          icon: $q.iconSet.editor.font,
                          fixedIcon: true,
                          list: 'no-icons',
                          options: ['default_font', 'arial', 'arial_black', 'comic_sans', 'courier_new', 'impact', 'lucida_grande', 'times_new_roman', 'verdana']
                        },
                        'removeFormat'
                      ],
                      ['quote', 'unordered', 'ordered', 'outdent', 'indent'],
                      ['undo', 'redo']
                    ]"
          :fonts="editorFonts">
          <template #speech>
            <div class="tw-flex tw-items-center tw-gap-1 tw-p-1 tw-border-1 dark:tw-border-bg-white/10">
              <div
                @click.stop="fieldUtil.startSpeechRecognition()"
                class=" tw-cursor-pointer tw-p-1.5 tw-rounded-md dark:hover:tw-bg-white/10 hover:tw-bg-black/10">
                <NamaIcon
                  :default-icon="fieldUtil.speech.isListening.value?'nm-wave':'nm-microphone'"
                  class="tw-h-full tw-size-4"/>
              </div>
              <q-btn-dropdown
                color="transparent"
                size="sm" dense :disable="fieldUtil.isFieldDisabled(forceEnableField) || !fieldUtil.speech.isSupported.value"
                class="q-btn-dropdown-dense tw-h-fit"
                unelevated>
                <q-list ref="qListRef">
                  <q-item
                    dense :class="{'selected-list-item tw-text-xs':fieldUtil.lang.value==item.value}"
                    @click="fieldUtil.onSpeechLanguageChange(item.value)"
                    clickable
                    v-close-popup
                    v-for="item in BigTextFieldWidgetUtil.speechLanguages">
                    <q-item-section>
                      <q-item-label v-text="Translator.translateId(item.value)"/>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </div>
          </template>
        </q-editor>
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </form>
    </template>
  </WidgetContainer>
</template>
